import { PayloadAction } from '@reduxjs/toolkit';
import moment, { Moment } from 'moment';

import { AntdTableResponseOfListOfEventLogDto, EventLogClient, EventLogDto, EventLogSearchDto } from '@medone/medonehp-api-client';

import { handleError, toastError } from '../../../../shared/common/HandleErrors';
import { getEnvBaseUrl } from '../../../../shared/common/helpers';
import { Axios } from '../../../../shared/common/http';
import { AppDispatch, AppThunk, RootState } from '../../../../shared/store';
import { AdminState } from './models';
import { setEventLogFilter, setEventLogs } from './slice';

export const reducers = {
    setEventLogs: (state: AdminState, action: PayloadAction<AntdTableResponseOfListOfEventLogDto>) => {
        state.eventLogs = action.payload;
        state.errorMessage = null;
    },

    setEventLogFilter: (state: AdminState, action: PayloadAction<EventLogSearchDto>) => {
        state.eventLogFilter = action.payload;
    },

    syncEventLog: (state: AdminState, action: PayloadAction<EventLogDto>) => {
        let newItems = [...state.eventLogs.entity];
        const index = state.eventLogs.entity.findIndex((x) => x.id === action.payload.id);

        if (index !== -1) {
            newItems = newItems.map((item) => {
                if (item.id === action.payload.id) {
                    return { ...item, ...action.payload } as EventLogDto;
                }

                return item;
            });
        } else {
            newItems.push(action.payload);
        }

        state.eventLogs.entity = newItems;
    },
};

export function fetchEventLogs(dto: EventLogSearchDto): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch(setEventLogFilter(dto));

        const client = new EventLogClient(null, Axios);

        try {
            const response = await client.getEventLogs(dto);

            if (response.result.succeeded) {
                dispatch(setEventLogs(response.result));
            } else {
                handleError(response);
            }
        } catch (error) {
            handleError(error);
        }
    };
}

// CustomDatePicker is a native <input type="date">, so antd form state holds "YYYY-MM-DD" strings
// for user-edited fields and Moments only for untouched initial values — normalize at every seam.
export const asMoment = (value: Moment | string): Moment => moment(value);

const exportFailedMessage = 'Export eRx evidence failed.';
const exportFallbackFileName = 'erx-audit-evidence.csv';

// Mirrors the SCRUM-701 evidence-api helper: filename* wins over filename, quotes stripped.
// Axios lowercases response header keys, so only the first probe is live today; the capitalized
// one is defensive insurance for the swap to the generated ErxEvidenceClient.
const parseFileName = (headers: unknown): string | undefined => {
    const bag = headers as Record<string, string> | undefined;
    const disposition = bag?.['content-disposition'] ?? bag?.['Content-Disposition'];

    if (!disposition) {
        return undefined;
    }

    const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);

    if (encoded?.[1]) {
        try {
            return decodeURIComponent(encoded[1].replace(/["']/g, '').trim());
        } catch {
            // Malformed percent-escapes count as unparseable — the static name is better than a throw.
            return undefined;
        }
    }

    const plain = /filename="?([^";]+)"?/i.exec(disposition);

    return plain?.[1]?.trim();
};

// responseType 'blob' makes a ProblemDetails body arrive as a Blob, so the backend's message has to
// be read out of it — otherwise a 403 and a rejected date range are indistinguishable to the user.
const readErrorMessage = async (error: unknown): Promise<string> => {
    try {
        const body = (error as { response?: { data?: unknown } })?.response?.data;
        // Guard, not narrowing: a missing body, or an engine without Blob.text, has to fall through
        // to the static message rather than throw.
        const text = typeof (body as Blob)?.text === 'function' ? await (body as Blob).text() : '';

        if (!text) {
            return exportFailedMessage;
        }

        const problem = JSON.parse(text) as { errors?: Record<string, string[]>; detail?: string; title?: string };
        const candidates = [Object.values(problem?.errors ?? {}).flat()[0], problem?.detail, problem?.title];

        // Plain string only — toastError renders JSX, so never hand it unescaped backend markup.
        return candidates.find((value) => typeof value === 'string' && value.trim().length > 0) ?? exportFailedMessage;
    } catch {
        // Empty body, an HTML error page, or an unreadable blob — the static message is the floor.
        return exportFailedMessage;
    }
};

// Raw Axios call (evidence-slice/SCRUM-701 precedent): swaps to the generated ErxEvidenceClient
// once @medone/medonehp-api-client republishes with it (SCRUM-806). Before swapping, confirm the
// generated params serialize as YYYY-MM-DD — the endpoint binds DateOnly and 400s an offset-bearing
// value, so an OffsetMomentJS toISOString() would break every click.
export function exportErxEvidence(from: Moment | string, to: Moment | string): AppThunk {
    return async () => {
        const fromDate = asMoment(from);
        const toDate = asMoment(to);

        try {
            // Absolute URL — the shared Axios request interceptor throws on relative paths.
            const response = await Axios.get(`${getEnvBaseUrl()}/api/ErxEvidence/Export`, {
                params: { from: fromDate.format('YYYY-MM-DD'), to: toDate.format('YYYY-MM-DD') },
                responseType: 'blob',
            });

            // Cancelled requests resolve without a response (interceptor contract) — stay silent.
            if (!response?.data) {
                return;
            }

            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);

            try {
                const a = document.createElement('a');

                a.href = url;
                // The backend owns the name (UTC-normalized window + generation timestamp), so never mirror it here.
                a.setAttribute('download', parseFileName(response.headers) || exportFallbackFileName);
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            } finally {
                // Deferred revoke off this tick, per SCRUM-921 (ee64c242): a same-tick revoke cancels
                // the download in WebKit. In `finally` so it still runs when the click throws.
                setTimeout(() => URL.revokeObjectURL(url), 0);
            }
        } catch (error) {
            // Not handleError: it deliberately swallows anything whose toString() contains
            // 'AxiosError', which would leave every HTTP failure here silent.
            toastError(await readErrorMessage(error));
        }
    };
}

export const selectEventLogs = (state: RootState) => state.admin.eventLogs;
